import { FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDialogA11y } from "components/dialog/useDialogA11y";
import { useWebinarUrl, MediaItem } from "lib/mediaQueries";
import "./WebinarPlayerModal.scss";

let webinarModalInstanceId = 0;

interface Props {
  /** The webinar to play, or null when the modal is closed. */
  item: MediaItem | null;
  onClose: () => void;
}

/**
 * Full-screen dialog that plays one webinar. On open it fetches a short-lived
 * presigned URL for the item and streams it directly from Spaces via a native
 * <video> element (Range/seek supported). Closing unmounts the video, which
 * stops the download.
 */
const WebinarPlayerModal: FC<Props> = ({ item, onClose }) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [titleId] = useState(() => {
    webinarModalInstanceId += 1;
    return `webinar-modal-title-${webinarModalInstanceId}`;
  });

  const isOpen = Boolean(item);
  const { data, isError, isLoading } = useWebinarUrl(item?.id ?? null);

  useDialogA11y({ isOpen, onClose, containerRef: dialogRef });

  if (!item) return null;

  return (
    <div className="webinar-modal" data-testid="webinar-modal">
      <div className="webinar-modal__backdrop" onClick={onClose} aria-hidden="true" />
      <div
        className="webinar-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
      >
        <div className="webinar-modal__header">
          <h4 id={titleId} className="webinar-modal__title">
            {item.title}
          </h4>
          <button
            type="button"
            className="webinar-modal__close"
            aria-label={t("Close")}
            onClick={onClose}
            data-testid="webinar-modal-close"
          />
        </div>

        <div className="webinar-modal__body">
          {isLoading && (
            <div className="webinar-modal__state">{t("common.loading")}</div>
          )}
          {isError && (
            <div className="webinar-modal__state">{t("webinars.playError")}</div>
          )}
          {data && (
            <video
              className="webinar-modal__video"
              src={data.url}
              controls
              autoPlay
              data-testid="webinar-video"
            >
              <track kind="captions" />
            </video>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebinarPlayerModal;
